import React from 'react';
import { Link } from 'react-router-dom';
import './MentalCapacity.css';

const MentalCapacity = () => {
  return (
    <div className="mental-capacity-page">
      {/* Hero */}
      <section className="mc-hero">
        <div className="mc-hero__inner">
          <nav className="mc-hero__breadcrumb">
            <Link to="/">Home</Link>
            <span className="mc-hero__breadcrumb-sep">/</span>
            <span className="mc-hero__breadcrumb-current">Mental Capacity &amp; the Law</span>
          </nav>
          <h1 className="mc-hero__title">Mental Capacity and the Legal Framework</h1>
        </div>
      </section>

      {/* Overview */}
      <section className="mc-section mc-section--white">
        <div className="mc-section__inner">
          <p className="mc-section__label">The Legal Framework</p>
          <h2 className="mc-section__title">The Mental Capacity Act 2005</h2>
          <p className="mc-section__desc">
            Lasting Powers of Attorney are governed by the Mental Capacity Act 2005, which provides
            the legal framework for making decisions on behalf of adults who lack the mental
            capacity to make those decisions themselves.
          </p>
          <p className="mc-section__desc">
            The Act is designed to protect individuals who may be unable to make their own
            decisions, while empowering them to plan ahead and appoint trusted people to act on
            their behalf. It applies to anyone aged 16 or over (though LPAs can only be made by
            those aged 18 or over) and covers a wide range of decisions relating to personal
            welfare, healthcare and financial affairs.
          </p>
        </div>
      </section>

      {/* Five Statutory Principles */}
      <section className="mc-section mc-section--alt">
        <div className="mc-section__inner">
          <p className="mc-section__label">Core Principles</p>
          <h2 className="mc-section__title">The Five Statutory Principles</h2>
          <p className="mc-section__desc">
            The Mental Capacity Act 2005 is built upon five fundamental principles that underpin
            all decisions made under the Act. These principles must be followed by anyone making
            decisions or acting on behalf of a person who may lack capacity.
          </p>
          <div className="mc-principles">
            <div className="mc-principle">
              <div className="mc-principle__number">1</div>
              <div className="mc-principle__content">
                <h4>Presumption of capacity</h4>
                <p>
                  Every adult is presumed to have capacity to make their own decisions unless it is
                  established that they lack capacity. No one should assume that a person cannot make
                  a decision simply because of their age, appearance, condition or behaviour.
                </p>
              </div>
            </div>
            <div className="mc-principle">
              <div className="mc-principle__number">2</div>
              <div className="mc-principle__content">
                <h4>Decision-specific and time-specific</h4>
                <p>
                  Capacity is assessed in relation to the specific decision that needs to be made, at
                  the specific time it needs to be made. A person may lack capacity to make one type
                  of decision but retain the ability to make others.
                </p>
              </div>
            </div>
            <div className="mc-principle">
              <div className="mc-principle__number">3</div>
              <div className="mc-principle__content">
                <h4>Right to make unwise decisions</h4>
                <p>
                  Individuals are entitled to make decisions that others might consider unwise or
                  eccentric. The fact that a person makes a decision that seems unwise does not, in
                  itself, mean that they lack the capacity to make that decision.
                </p>
              </div>
            </div>
            <div className="mc-principle">
              <div className="mc-principle__number">4</div>
              <div className="mc-principle__content">
                <h4>Best interests</h4>
                <p>
                  Any decision made, or action taken, on behalf of a person who lacks capacity must
                  be made in their best interests. This requires consideration of all relevant
                  circumstances, including the person's past and present wishes, feelings, beliefs
                  and values.
                </p>
              </div>
            </div>
            <div className="mc-principle">
              <div className="mc-principle__number">5</div>
              <div className="mc-principle__content">
                <h4>Least restrictive option</h4>
                <p>
                  Before any decision is made or action taken, consideration must be given to
                  whether the purpose can be achieved in a way that is less restrictive of the
                  person's rights and freedom of action. The least restrictive option should always
                  be preferred.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capacity to Create an LPA */}
      <section className="mc-section mc-section--white">
        <div className="mc-section__inner">
          <p className="mc-section__label">Creating an LPA</p>
          <h2 className="mc-section__title">Capacity to Create an LPA</h2>
          <p className="mc-section__desc">
            To create a valid Lasting Power of Attorney, you must have the mental capacity to
            understand what you are doing. Specifically, you must understand:
          </p>
          <ul className="mc-list">
            <li>
              The nature and effect of the LPA, including what powers you are granting and the
              consequences of doing so
            </li>
            <li>
              The authority you are granting to your chosen attorneys, including the scope of
              decisions they will be able to make on your behalf
            </li>
            <li>
              That your attorneys must always act in your best interests and in accordance with
              the Mental Capacity Act 2005
            </li>
          </ul>
          <div className="mc-note">
            <p>
              This is why it is important to make an LPA while you have capacity, rather than
              waiting until capacity may be in question. Once you lose the capacity to understand
              these matters, it will no longer be possible to create a valid LPA.
            </p>
          </div>
        </div>
      </section>

      {/* Certificate Provider */}
      <section className="mc-section mc-section--alt">
        <div className="mc-section__inner">
          <p className="mc-section__label">Safeguard</p>
          <h2 className="mc-section__title">The Role of the Certificate Provider</h2>
          <p className="mc-section__desc">
            The certificate provider is an essential safeguard in the LPA process. Their role is to
            provide an independent confirmation that the person making the LPA understands what they
            are doing and is not being pressured into it.
          </p>
          <div className="mc-cert">
            <h3 className="mc-cert__title">What the Certificate Provider confirms</h3>
            <p className="mc-cert__text">
              The certificate provider must confirm that, in their opinion, the donor understands
              the purpose and scope of the LPA, that no fraud or undue pressure is being used to
              induce the donor to create the LPA, and that there is nothing else that would prevent
              the LPA from being validly created.
            </p>
            <p className="mc-cert__text">
              The certificate provider must be someone who has known the donor personally for at
              least two years, or a professional person such as a solicitor, doctor, or other
              qualified individual. They cannot be a family member, an attorney named in the LPA,
              or a member of the donor's or attorney's family.
            </p>
          </div>
        </div>
      </section>

      {/* Joint vs Joint and Several */}
      <section className="mc-section mc-section--white">
        <div className="mc-section__inner">
          <p className="mc-section__label">Attorney Appointments</p>
          <h2 className="mc-section__title">Joint vs Joint and Several Attorneys</h2>
          <p className="mc-section__desc">
            When appointing more than one attorney, you must decide how they will work together.
            The two options are to appoint them jointly, or jointly and severally. This decision
            has significant practical implications.
          </p>
          <div className="mc-comparison">
            <div className="mc-comparison__card">
              <span className="mc-comparison__badge">Option A</span>
              <h3 className="mc-comparison__title">Jointly</h3>
              <ul className="mc-comparison__list">
                <li>All attorneys must make every decision together and must all agree</li>
                <li>No single attorney can act independently on any matter</li>
                <li>
                  If one attorney can no longer act (for example, due to death, loss of capacity, or
                  resignation), the entire LPA will fail unless a replacement attorney has been
                  appointed
                </li>
                <li>
                  This option provides the greatest level of collective oversight but is less
                  flexible in practice
                </li>
              </ul>
            </div>
            <div className="mc-comparison__card">
              <span className="mc-comparison__badge">Option B</span>
              <h3 className="mc-comparison__title">Jointly and Severally</h3>
              <ul className="mc-comparison__list">
                <li>
                  Attorneys can make decisions together or independently, providing greater
                  flexibility
                </li>
                <li>
                  Any one attorney can act alone without needing the agreement of the others
                </li>
                <li>
                  If one attorney can no longer act, the remaining attorneys can continue to make
                  decisions under the LPA
                </li>
                <li>
                  This option is generally more practical and is the more commonly chosen
                  arrangement
                </li>
              </ul>
            </div>
          </div>
          <div className="mc-note">
            <p>
              It is also possible to appoint attorneys to act jointly for some decisions and jointly
              and severally for others. We can advise you on the most appropriate arrangement for
              your particular circumstances.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mc-cta">
        <div className="mc-cta__inner">
          <h2 className="mc-cta__title">Need Guidance on Mental Capacity?</h2>
          <p className="mc-cta__desc">
            Understanding the legal framework is the first step towards protecting yourself and
            your loved ones. Contact us for clear, professional advice.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MentalCapacity;
